import { MutableRefObject, forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { Theme } from '../../types/enums';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { useBreakpoint } from '../Breakpoint/BreakpointProvider';
import styles from './Tooltip.module.scss';
import { TooltipPreference } from './types/enums';
import {
  LRDirConfig,
  BaseDirConfigPropType,
  NextLRDirConfig,
  TBDirConfigProptype,
  TooltipPropsTypes,
} from './types/propsType';

const cx = classNames.bind(styles);

const { LEFT, RIGHT, TOP_CENTER, TOP_LEFT, TOP_RIGHT, BOTTOM_CENTER, BOTTOM_LEFT, BOTTOM_RIGHT } = TooltipPreference;
const filterDirection = (inputObject: LRDirConfig): BaseDirConfigPropType | undefined =>
  Object.entries(inputObject).find((entry) => {
    const { isDirection } = entry[1] as BaseDirConfigPropType;
    return isDirection;
  })?.[1];
function getLRDirection(
  currentDirConfig: LRDirConfig,
  nextDirConfig: NextLRDirConfig,
  canCompleteTop: boolean,
  canCompleteBottom: boolean,
  preference: string,
) {
  const currentDir = filterDirection(currentDirConfig),
    nextDir = filterDirection(nextDirConfig);
  if (currentDir) {
    return currentDir.direction;
  } else if (canCompleteTop) {
    return TOP_CENTER;
  } else if (canCompleteBottom) {
    return BOTTOM_CENTER;
  } else if (nextDir) {
    return nextDir.direction;
  }
  return preference;
}
function getTBDirection(
  fallbackPrimaryDir: BaseDirConfigPropType,
  fallbackSecondaryDir: BaseDirConfigPropType,
  currentDirConfig: TBDirConfigProptype,
  nextDirconfig: TBDirConfigProptype,
  prefernce: string,
): string {
  if (currentDirConfig.subsequentPrimaryDir?.isDirection) {
    return currentDirConfig.subsequentPrimaryDir.direction;
  } else if (currentDirConfig.subsequentSecondaryDir?.isDirection) {
    return currentDirConfig.subsequentSecondaryDir.direction;
  } else if (fallbackPrimaryDir.isDirection) {
    return fallbackPrimaryDir.direction;
  } else if (fallbackSecondaryDir.isDirection) {
    return fallbackSecondaryDir.direction;
  } else if (nextDirconfig.subsequentPrimaryDir?.isDirection) {
    return nextDirconfig.subsequentPrimaryDir.direction;
  } else if (nextDirconfig.subsequentSecondaryDir?.isDirection) {
    return nextDirconfig.subsequentSecondaryDir.direction;
  } else if (nextDirconfig.isDirection) {
    return nextDirconfig.defaultDir;
  }
  return prefernce;
}
function getTBPayload(positioningFlag: Record<string, boolean>, primaryDirection: string, secondaryDirection: string) {
  const {
    canTop,
    canCompleteTop,
    canCompleteBottom,
    canBottom,
    canCompleteRight,
    canTopBottomRight,
    canCompleteLeft,
    canTopBottomLeft,
  } = positioningFlag;
  const completeLeft = {
      direction: LEFT,
      isDirection: canCompleteLeft,
    },
    completeRight = {
      direction: RIGHT,
      isDirection: canCompleteRight,
    },
    topRight = {
      isDirection: canTop && canTopBottomRight,
      direction: TOP_RIGHT,
    },
    topLeft = {
      isDirection: canTop && canTopBottomLeft,
      direction: TOP_LEFT,
    },
    bottomRight = {
      isDirection: canBottom && canTopBottomRight,
      direction: BOTTOM_RIGHT,
    },
    bottomLeft = {
      isDirection: canBottom && canTopBottomLeft,
      direction: BOTTOM_LEFT,
    };
  // Default payload for TOP_LEFT;
  let fallbackPrimaryDir: BaseDirConfigPropType = completeLeft,
    fallbackSecondaryDir: BaseDirConfigPropType = completeRight;
  const currentDirConfig: TBDirConfigProptype = {
      defaultDir: TOP_CENTER,
      isDirection: canTop && (canTopBottomRight || canTopBottomLeft),
      subsequentPrimaryDir: topLeft,
      subsequentSecondaryDir: topRight,
    },
    nextDirconfig: TBDirConfigProptype = {
      defaultDir: BOTTOM_CENTER,
      isDirection: canCompleteBottom,
      subsequentPrimaryDir: {
        isDirection: canTopBottomLeft,
        direction: BOTTOM_LEFT,
      },
      subsequentSecondaryDir: { isDirection: canTopBottomRight, direction: BOTTOM_RIGHT },
    };
  if (secondaryDirection === RIGHT) {
    fallbackPrimaryDir = completeRight;
    fallbackSecondaryDir = completeLeft;
    currentDirConfig.subsequentPrimaryDir = topRight;
    currentDirConfig.subsequentSecondaryDir = topLeft;
  }
  if (primaryDirection === 'bottom') {
    currentDirConfig.defaultDir = BOTTOM_CENTER;
    currentDirConfig.isDirection = canBottom && (canTopBottomRight || canTopBottomLeft);
    nextDirconfig.defaultDir = TOP_CENTER;
    nextDirconfig.isDirection = canCompleteTop;
    nextDirconfig.subsequentPrimaryDir = topLeft;
    nextDirconfig.subsequentSecondaryDir = topRight;
    if (secondaryDirection === LEFT) {
      currentDirConfig.subsequentPrimaryDir = bottomLeft;
      currentDirConfig.subsequentSecondaryDir = bottomRight;
    } else {
      currentDirConfig.subsequentPrimaryDir = bottomRight;
      currentDirConfig.subsequentSecondaryDir = bottomLeft;
    }
  }
  return {
    fallbackPrimaryDir,
    fallbackSecondaryDir,
    currentDirConfig,
    nextDirconfig,
  };
}
function getLRPayload(positioningFlag: Record<string, boolean>, direction: string) {
  const { canTop, canBottom, canCompleteRight, canTopBottomRight, canCompleteLeft, canTopBottomLeft } = positioningFlag;
  const completeLeft = {
      direction: LEFT,
      isDirection: canCompleteLeft,
    },
    completeRight = {
      direction: RIGHT,
      isDirection: canCompleteRight,
    },
    topRight: BaseDirConfigPropType = {
      isDirection: canTop && canTopBottomRight,
      direction: TOP_RIGHT,
    },
    topLeft = {
      isDirection: canTopBottomLeft,
      direction: TOP_LEFT,
    },
    bottomRight = {
      isDirection: canBottom && canTopBottomRight,
      direction: BOTTOM_RIGHT,
    },
    bottomLeft = {
      isDirection: canTopBottomLeft,
      direction: BOTTOM_LEFT,
    };
  let currentDirConfig: LRDirConfig = {
      primaryDir: topRight,
      secondaryDir: bottomRight,
    },
    nextDirConfig: NextLRDirConfig = {
      primaryDir: topLeft,
      secondaryDir: completeLeft,
      tertiaryDir: bottomLeft,
    };
  if (direction === LEFT) {
    currentDirConfig = {
      primaryDir: topLeft,
      secondaryDir: bottomLeft,
    };
    nextDirConfig = { primaryDir: topRight, secondaryDir: completeRight, tertiaryDir: bottomRight };
  }
  return { currentDirConfig, nextDirConfig };
}

/**
 *
 * @description Tooltip Provides a brief, informative message that
 *  appears when a user interacts with an information icon.
 */

const Tooltip = forwardRef<HTMLSpanElement, TooltipPropsTypes>(
  (
    {
      preference = TooltipPreference.TOP_CENTER,
      message,
      className = '',
      iconName = 'circle-info',
      theme = Theme.PURPLE,
      iconStyle = IconStyle.REGULAR,
    }: TooltipPropsTypes,
    ref,
  ) => {
    const tooltipRef = useRef() as MutableRefObject<HTMLDivElement>;
    const tooltipIconRef = useRef() as MutableRefObject<HTMLButtonElement>;
    const viewport = useBreakpoint();
    const [currentDirection, setCurrentDirection] = useState('');
    const { clientWidth: viewportWidth, clientHeight: viewportHeight } = document.documentElement;

    useImperativeHandle(ref, () => tooltipRef.current);

    const handleMouseOver = useCallback(() => {
      if (tooltipRef.current && tooltipIconRef.current) {
        getTooltipLocation(tooltipRef.current.getBoundingClientRect(), tooltipIconRef.current.getBoundingClientRect());
      }
    }, [tooltipRef, tooltipIconRef, viewport, preference, message]);

    /**
     *
     * @param newDirection string from location calcultaion
     * @description function runs in case when no appropritate location found to display tooltip.
     */
    function processPreferneceUI(newDirection: string) {
      if (tooltipRef.current && preference === newDirection) {
        // 48px + 48px (left & right padding)
        tooltipRef.current.style.maxWidth = `${viewportWidth - 48 * 2}px`;
      }
      setCurrentDirection(newDirection);
    }

    /**
     *
     * @param messageRef tooltip message cloud reference
     * @param iconRef tooltip display icon ref.
     * @description function provide appropriate location for tooltip
     */
    function getTooltipLocation(messageRef: DOMRect, iconRef: DOMRect) {
      const {
        height: tooltipMessageHeight,
        width: tooltipMessageWidth,
        left: toolTipLeft,
        right: toolTipRight,
      } = messageRef;
      const {
        bottom: tooltipIconBottom,
        left: tooltipIconLeft,
        right: tooltipIconRight,
        top: tooltipIconTop,
      } = iconRef;

      const [primaryDirection, secondaryDirection] = preference.split('-');
      const isClipping = toolTipRight <= viewportWidth && toolTipLeft >= 0;
      // 15 is distance between tooltip icon and tooltip message box.
      const positioningFlag = {
        canCompleteTop: isClipping && tooltipMessageHeight + 15 <= tooltipIconTop,
        canCompleteBottom: isClipping && tooltipMessageHeight + 15 <= viewportHeight - tooltipIconBottom,
        canCompleteRight: viewportWidth > tooltipMessageWidth + tooltipIconRight + 15,
        canCompleteLeft: tooltipIconLeft > tooltipMessageWidth + 15,
        canTop: tooltipMessageHeight + 15 <= tooltipIconTop,
        canBottom: tooltipMessageHeight + 15 <= viewportHeight - tooltipIconBottom,
        canTopBottomRight: viewportWidth >= tooltipMessageWidth + tooltipIconRight,
        canTopBottomLeft: tooltipIconRight >= tooltipMessageWidth,
      };
      const { canCompleteTop, canCompleteBottom, canCompleteRight, canCompleteLeft } = positioningFlag;
      switch (primaryDirection) {
        case RIGHT:
          {
            if (canCompleteRight) {
              setCurrentDirection(RIGHT);
              break;
            }
            const { currentDirConfig, nextDirConfig } = getLRPayload(positioningFlag, primaryDirection);
            const newDirection = getLRDirection(
              currentDirConfig,
              nextDirConfig,
              canCompleteTop,
              canCompleteBottom,
              preference,
            );
            processPreferneceUI(newDirection);
          }
          break;
        case LEFT:
          {
            if (canCompleteLeft) {
              setCurrentDirection(LEFT);
              break;
            }
            const { currentDirConfig, nextDirConfig } = getLRPayload(positioningFlag, primaryDirection);
            const newDirection = getLRDirection(
              currentDirConfig,
              nextDirConfig,
              canCompleteTop,
              canCompleteBottom,
              preference,
            );
            processPreferneceUI(newDirection);
          }
          break;
        case 'top':
          {
            if (canCompleteTop && secondaryDirection === 'center') {
              setCurrentDirection(TOP_CENTER);
              break;
            }
            const { fallbackPrimaryDir, fallbackSecondaryDir, currentDirConfig, nextDirconfig } = getTBPayload(
              positioningFlag,
              'top',
              secondaryDirection,
            );
            const newDirection = getTBDirection(
              fallbackPrimaryDir,
              fallbackSecondaryDir,
              currentDirConfig,
              nextDirconfig,
              preference,
            );
            processPreferneceUI(newDirection);
          }
          break;

        default:
          {
            if (canCompleteBottom && secondaryDirection === 'center') {
              setCurrentDirection(BOTTOM_CENTER);
              break;
            }
            const { fallbackPrimaryDir, fallbackSecondaryDir, currentDirConfig, nextDirconfig } = getTBPayload(
              positioningFlag,
              'bottom',
              secondaryDirection,
            );
            const newDirection = getTBDirection(
              fallbackPrimaryDir,
              fallbackSecondaryDir,
              currentDirConfig,
              nextDirconfig,
              preference,
            );
            processPreferneceUI(newDirection);
          }
          break;
      }
    }
    const CClassname = cx('tooltip', [currentDirection, theme], className);
    return (
      <button
        id="nextgen-tooltip-component"
        className={CClassname}
        data-testid="tooltip-test-id"
        onMouseEnter={handleMouseOver}
        onMouseLeave={() => setCurrentDirection('')}
        ref={tooltipIconRef}
      >
        <FAIcon iconName={iconName} theme={theme} className={styles['tooltip-info']} iconStyle={iconStyle} />
        <span className={styles['tooltip-text']} ref={tooltipRef} data-testid="tooltip-text-body-testid">
          <span data-testid="tooltip-message-test">{message}</span>
        </span>
      </button>
    );
  },
);
Tooltip.displayName = 'Tooltip';

export { TooltipPreference };
export default Tooltip;
